import { Backdrop, CircularProgress, Container, Divider, Grid } from '@material-ui/core';
import { ArrowBack, Save as SaveIcon } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Success from '../../components/Form/Success';
import ButtonUI from '../../components/ui/ButtonUI';
import PageTitle from '../../components/ui/PageTitleUI';
import { useAuth } from '../../hooks/auth';
import { InfoMvmAddInvoices, MvmInvoice } from '../../interfaces/Mvm';
import MvmStatus from '../../interfaces/MvmStatus';
import MvmApi from '../../services/MvmApi';
import permissions from '../../utils/Permissions';
import MvmInvoiceList from './components/MvmInvoiceList';
import MvmSummary from './components/MvmSummary';
import { useStyles } from './Style';

interface IFormInput {
    mvmNumber: any;
    invoices: any;
}

const opcoes = [
    { url: '/AddMvmInvoice', descricao: 'Incluir NF ao MVM', state: {}},
    { url: 'printMvm', descricao: 'Imprimir Mvm', state: {}},
    { url: '/mvm', descricao: 'Pesquisar Mvm', state: {}},
]

const AddMvmInvoice: React.FC = () => {
    
    const classes = useStyles();
    const { user, hasPermission } = useAuth()
    const history = useHistory()
    const { t } = useTranslation("Mvm")
    const { t: _t } = useTranslation("General")
    const { enqueueSnackbar } = useSnackbar()
    const [mvm, setMvm] = useState<any>(null)
    const [circularLoading, setCircularLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const { control, handleSubmit, setValue, getValues, watch } = useForm<IFormInput>(
    {
        defaultValues: { mvmNumber: {}, invoices: [] }
    })

    const canDeleteInvoice = hasPermission(permissions.MVM.DeleteInvoice);

    const onSummaryChange = (data: any) => {
        
        if (!data) {
            setMvm(null)
            return
        }

        setMvm(data)
    }

    useEffect(() => {
        if (!hasPermission(permissions.MVM.Invoice))
            history.replace('NoPermission');
    }, [history, user])

    useEffect(() => {

        if (mvm && mvm.invoices) {
            // O id do objeto entra em conflito com o id gerado pelo fieldarray dos componentes de nota fiscal. 
            // Criei esse parâmetro para manter o id da nota fiscal salvo, ele é recuperado no save posteriormente
            for (let i = 0; i < mvm.invoices.length; i++) {
                mvm.invoices[i].mvmInvoiceId = mvm.invoices[i].id 
            }
        }

        setValue("invoices", mvm ? mvm.invoices : [])
    }, [mvm])

    const validateInvoiceFiles = (data: any) => {

        // Verificar se a nota está em contingência (tpEmis)
        // O caractere que indica se a nota foi emitida em contingência é o 34
        // 1 = Emissão normal (não em contingência)
        // 2 = Contingência FS-IA, com impressão do DANFE em formulário de segurança
        // 3 = Contingência SCAN (Sistema de Contingência do Ambiente Nacional)
        // 4 = Contingência DPEC (Declaração Prévia da Emissão em Contingência)
        // 5 = Contingência FS-DA, com impressão do DANFE em formulário de segurança
        // 6 = Contingência SVC-AN (SEFAZ Virtual de Contingência do AN)
        // 7 = Contingência SVC-RS (SEFAZ Virtual de Contingência do RS)
        const isContingency = (invoiceKey: string) : boolean => {
            return invoiceKey.substring(34, 35) !== '1'
        }

        var invoices = data.invoices
        
        for (let i = 0; i < invoices.length; i++) {
            if (isContingency(invoices[i].invoiceKey) && !invoices[i].hasFile)
                return false
        }
        
        return true
    }

    async function addMvmInvoices() {

        if (!mvm) {
            enqueueSnackbar(t("returnSearchMvmNotFoundMessage"), { variant: 'warning' })
            return
        }

        if (mvm.composition && mvm.mvmType !== 'C') {
            enqueueSnackbar(t("mvmCabinCannotContainInvoices"), { variant: 'warning' })
            return
        }
        
        var data = getValues()

        if (!validateInvoiceFiles(data)) {
            enqueueSnackbar(_t("contingencyInvoicesWithoutFilesValidation"), { variant: 'warning' }) 
            return
        }

        setCircularLoading(true)

        // Recuperação do id original dos objetos. Caso eles tenham sido editados no componente o id original é trocado por um guid.
        for (let i = 0; i < data.invoices.length; i++) {
            if (data.invoices[i].mvmInvoiceId)
                data.invoices[i].id = data.invoices[i].mvmInvoiceId 
            else
                data.invoices[i].id = 0
        }

        let putObject: InfoMvmAddInvoices = { id: mvm.id, invoices: [] }

        // Enviar apenas o contrato esperado pelo endpoint para evitar falha de conversao em campos extras (ex.: invoiceCfop = null)
        data.invoices.forEach((e: MvmInvoice) =>
            putObject.invoices.push({
                id: e.id,
                invoiceKey: e.invoiceKey,
                invoiceType: e.invoiceType,
                invoiceNumber: e.invoiceNumber,
                invoiceDate: e.invoiceDate,
                invoiceSupplierId: e.invoiceSupplierId,
                invoiceSupplierCorporateName: e.invoiceSupplierCorporateName,
            })
        )

        await MvmApi.addMvmInvoices(putObject)
            .then(() => {
                enqueueSnackbar(_t("returnSubmitEditFormSuccess"), { variant: 'success' })
                setSuccess(true);
                setCircularLoading(false)
            })
            .catch(()=> {
                enqueueSnackbar(_t('returnSubmitEditFormError'), { variant: 'error'});
                setCircularLoading(false)
            })
    }

    const onSubmit = (data: any) => { }

    if (success)
        return (
            <Container maxWidth={false} component="main">
                <div className={classes.rootContent}>
                    <div className={classes.paper}>
                        <Success opcoes={opcoes} MvmId={mvm.id}/>
                    </div>
                </div>
            </Container>
        )
    else
    return (
        <Container maxWidth={false} component="main">
    
            <PageTitle title={t("mvmInvoiceInsert") + (mvm ? (" - " + mvm.mvmNumber) : "")} />
            <div className={classes.rootContent}>
                <form onSubmit={handleSubmit(onSubmit)} >      
                    <Backdrop style={{ zIndex: 9999 }} open={circularLoading}>
                        <CircularProgress color="inherit" />
                    </Backdrop>              
                    <Divider style={{ marginTop: '10px' }} />
                    <MvmSummary
                        control={control}
                        name={"summary"}
                        onChange={onSummaryChange}
                        setValue={setValue}
                        mvmStatus={[MvmStatus.FLAG_STATUS_PENDING_ENTRANCE]}
                        includeProperties={["Invoices"]}
                    />
                    {!mvm ? <></> :
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <MvmInvoiceList
                                    control={control}
                                    name={"invoices"}
                                    allowAdd={true}
                                    allowDelete={canDeleteInvoice}
                                    Mvm={mvm} />
                            </Grid>
                            <ButtonUI
                                style={{ marginTop: 15, marginLeft: 15 }}
                                variant="outlined"
                                color="secondary"
                                onClick={() => window.location.reload()}
                                startIcon={<ArrowBack />}>
                                {_t("back")}
                            </ButtonUI>
                            <ButtonUI
                                style={{ marginTop: 15, marginLeft: 15 }}
                                variant="outlined"
                                color="primary"
                                onClick={() => addMvmInvoices()}
                                startIcon={<SaveIcon />}>
                                {_t("save")}
                            </ButtonUI>
                        </Grid>
                    }
                </form>
            </div>
        </Container>
    )
}
export default AddMvmInvoice
